import React, { useMemo, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Select from '~components/Form/components/Select';
import InputField from '~components/Form/components/InputField';
import NavTabs from '~components/NavTabs';
import ModalAction from '~components/Modal/ModalAction';
import { useConfigureFooter } from '~hooks/useHelpers';
import { initialPath } from '~misc/paths';
import { readFunnelGroups, updateFunnelGroup } from '../../hooks/useFunnelMappingGroups';
import { mockMapeamentos } from '../../mocks/mockMapeamentos';

const grupoProdutoOptions = [
  { label: 'Cimento', value: 1, unidadeMedidaId: 1 },
  { label: 'Concreto', value: 2, unidadeMedidaId: 2 },
  { label: 'Básicas Ensacados', value: 3, unidadeMedidaId: 1 },
  { label: 'Básicas Granel', value: 4, unidadeMedidaId: 1 },
  { label: 'Colantes', value: 5, unidadeMedidaId: 1 },
  { label: 'Agregados', value: 6, unidadeMedidaId: 1 },
  { label: 'Argamassa', value: 7, unidadeMedidaId: 1 },
  { label: 'Cal', value: 8, unidadeMedidaId: 1 },
  { label: 'Gesso', value: 9, unidadeMedidaId: 1 },
  { label: 'Aditivos', value: 10, unidadeMedidaId: 3 },
  { label: 'Tijolos', value: 11, unidadeMedidaId: 1 },
  { label: 'Blocos', value: 12, unidadeMedidaId: 1 },
  { label: 'Pisos e Revestimentos', value: 13, unidadeMedidaId: 2 },
  { label: 'Tintas', value: 14, unidadeMedidaId: 3 },
  { label: 'Telhas', value: 15, unidadeMedidaId: 1 },
  { label: 'Outros', value: 16, unidadeMedidaId: 1 },
];

const medidaOptions = [
  { label: 'Tonelada (t)', value: 1 },
  { label: 'Metro Cúbico (m³)', value: 2 },
  { label: 'Litros (l)', value: 3 },
];

const novoMapeamentoInitialValues = {
  funilObraGrupoProdutoId: '',
  dataNegociacao: '',
  unidadeMedidaId: '',
  volumeMapeado: '',
  volumeNegociado: '',
};

const baseSchemaShape = {
  funilObraGrupoProdutoId: yup
    .number()
    .typeError('Grupo Produto é obrigatório')
    .required('Grupo Produto é obrigatório'),
  unidadeMedidaId: yup
    .number()
    .typeError('Unidade de Medida é obrigatória')
    .required('Unidade de Medida é obrigatória'),
  volumeMapeado: yup
    .number()
    .typeError('Volume Mapeado é obrigatório')
    .required('Volume Mapeado é obrigatório'),
};

const novoMapeamentoSchema = yup.object({
  ...baseSchemaShape,
  dataNegociacao: yup.string().required('Data de Negociação é obrigatória'),
  volumeNegociado: yup
    .number()
    .typeError('Volume Negociado é obrigatório')
    .required('Volume Negociado é obrigatório'),
});

const acompanhamentoSchema = yup.object(baseSchemaShape);

const modalKind = {
  save: 'save',
};

const extractUnidadeAbreviacao = (label = '') => {
  const match = label.match(/\(([^)]+)\)/);
  return match ? match[1] : label;
};

const buildAcompanhamentoInitialValues = (group) => ({
  funilObraGrupoProdutoId: group?.funilObraGrupoProdutoId ?? '',
  unidadeMedidaId: group?.unidadeMedidaId ?? '',
  volumeMapeado: group?.volumeMapeado ?? '',
});

const FunnelEdit = () => {
  const formikRef = useRef();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { parameter: clienteId, id } = useParams();

  const [activeModal, setActiveModal] = useState(null);

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const fromAcompanhamento = queryParams.get('from') === 'acompanhamento';
  const groupIdParam = queryParams.get('groupId');
  const groupId = groupIdParam ? Number(groupIdParam) : null;

  const acompanhamentoGroup = useMemo(() => {
    if (!fromAcompanhamento || groupId == null) return null;
    const stored = readFunnelGroups(clienteId);
    if (!Array.isArray(stored)) return null;
    return stored.find((group) => group.id === groupId) ?? null;
  }, [clienteId, fromAcompanhamento, groupId]);

  const isEdit = Boolean(id);
  const initialValues = fromAcompanhamento
    ? buildAcompanhamentoInitialValues(acompanhamentoGroup)
    : novoMapeamentoInitialValues;
  const validationSchema = fromAcompanhamento ? acompanhamentoSchema : novoMapeamentoSchema;

  const mappedGrupoIds = useMemo(
    () => new Set(mockMapeamentos.map((m) => Number(m.funilObraGrupoProdutoId))),
    []
  );

  const grupoProdutoOptionsFiltered = useMemo(() => {
    if (fromAcompanhamento || isEdit) return grupoProdutoOptions;
    return grupoProdutoOptions.filter((opt) => !mappedGrupoIds.has(opt.value));
  }, [fromAcompanhamento, isEdit, mappedGrupoIds]);

  const tabs = [
    {
      name: 'Funil de Obras',
      link: globalThis.location.pathname,
    },
  ];

  const handleChangeGrupoProduto = (option) => {
    if (!option) return;
    const formik = formikRef.current;
    if (!formik) return;
    formik.setFieldValue('funilObraGrupoProdutoId', option.value, true);
    formik.setFieldValue('unidadeMedidaId', option.unidadeMedidaId, true);
    formik.setFieldTouched('funilObraGrupoProdutoId', true, false);
    formik.setFieldTouched('unidadeMedidaId', true, false);
  };

  const handleChangeMedida = (option) => {
    if (!option) return;
    const formik = formikRef.current;
    if (!formik) return;
    formik.setFieldValue('unidadeMedidaId', option.value, true);
    formik.setFieldTouched('unidadeMedidaId', true, false);
  };

  const closeModal = () => setActiveModal(null);

  const persistAcompanhamento = (values) => {
    if (!acompanhamentoGroup) return;

    const grupoSelecionado = grupoProdutoOptions.find(
      (item) => item.value === Number(values.funilObraGrupoProdutoId)
    );
    const medidaSelecionada = medidaOptions.find(
      (item) => item.value === Number(values.unidadeMedidaId)
    );

    const novoVolumeMapeado = Number(values.volumeMapeado);
    const itemsAtualizados = (acompanhamentoGroup.items ?? []).map((item, idx) =>
      idx === 0 ? { ...item, volume: String(novoVolumeMapeado) } : item
    );

    updateFunnelGroup(clienteId, acompanhamentoGroup.id, {
      funilObraGrupoProdutoId: Number(values.funilObraGrupoProdutoId),
      unidadeMedidaId: Number(values.unidadeMedidaId),
      volumeMapeado: novoVolumeMapeado,
      title: grupoSelecionado ? grupoSelecionado.label.toUpperCase() : acompanhamentoGroup.title,
      unidade: medidaSelecionada
        ? extractUnidadeAbreviacao(medidaSelecionada.label)
        : acompanhamentoGroup.unidade,
      items: itemsAtualizados,
    });
  };

  const handleSubmit = () => {
    setActiveModal(modalKind.save);
  };

  const confirmarSalvamento = () => {
    if (fromAcompanhamento) {
      persistAcompanhamento(formikRef.current?.values ?? {});
    }
    closeModal();
    
    if (!fromAcompanhamento) {
      navigate(`${initialPath}/funil-de-obras/${clienteId}/mapeamento`);
    } else {
      navigate(`${initialPath}/funil-de-obras/${clienteId}/acompanhamento-mapeamento`);
    }
  };

  useConfigureFooter({ form: true, formId: 'novo', submitLabel: 'Mapear', disabled: false });

  return (
    <section className="container">
      <NavTabs navs={tabs} customClasses={'!flex-none !w-[40%]'} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        innerRef={formikRef}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched }) => {
          const grupoProdutoSelecionado =
            grupoProdutoOptions.find(
              (item) => item.value === Number(values.funilObraGrupoProdutoId)
            ) ?? null;
          const medidaSelecionada =
            medidaOptions.find(
              (item) => item.value === Number(values.unidadeMedidaId)
            ) ?? null;

          const onSelectGrupoProduto = (option) => {
            if (!option) return;
            setFieldTouched('funilObraGrupoProdutoId', true, false);
            setFieldValue('funilObraGrupoProdutoId', option.value, true);
            if (option.unidadeMedidaId != null) {
              setFieldTouched('unidadeMedidaId', true, false);
              setFieldValue('unidadeMedidaId', option.unidadeMedidaId, true);
            }
          };

          const onSelectMedida = (option) => {
            if (!option) return;
            setFieldTouched('unidadeMedidaId', true, false);
            setFieldValue('unidadeMedidaId', option.value, true);
          };

          return (
            <Form id="novo">
              <div className="relative containerForm mt-4 rounded-xl bg-white p-7">

                <div className="sm:flex justify-between py-3">
                  <h2 className="text-2xl font-bold title pb-6">
                    Dados Gerais
                  </h2>
                </div>

                {fromAcompanhamento ? (
                  <div className="grid grid-cols-2 gap-4">

                    <Select
                      options={grupoProdutoOptions}
                      label="Grupo Produto:"
                      name="funilObraGrupoProdutoId"
                      placeholder="Grupo Produto"
                      value={grupoProdutoSelecionado}
                      onChange={onSelectGrupoProduto}
                      isDisabled
                      isRequired
                      spacingControl={true}
                      errors={errors.funilObraGrupoProdutoId}
                      touched={touched.funilObraGrupoProdutoId}
                    />

                    <div />

                    <Select
                      options={medidaOptions}
                      label="Unidade de Medida:"
                      name="unidadeMedidaId"
                      placeholder="Unidade de Medida"
                      value={medidaSelecionada}
                      onChange={onSelectMedida}
                      isDisabled
                      isRequired
                      spacingControl={true}
                      errors={errors.unidadeMedidaId}
                      touched={touched.unidadeMedidaId}
                    />

                    <InputField
                      labelName="Volume Mapeado:"
                      tooltip="Volume total estimado para obra"
                      inputName="volumeMapeado"
                      inputMask="offmask"
                      inputType="number"
                      isRequired
                      placeholder="Volume Mapeado"
                      onWheel={(e) => e.target.blur()}
                    />

                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">

                    <Select
                      options={grupoProdutoOptionsFiltered}
                      label="Grupo Produto:"
                      name="funilObraGrupoProdutoId"
                      placeholder="Grupo Produto"
                      value={grupoProdutoSelecionado}
                      onChange={onSelectGrupoProduto}
                      isDisabled={isEdit}
                      isRequired
                      spacingControl={true}
                      errors={errors.funilObraGrupoProdutoId}
                      touched={touched.funilObraGrupoProdutoId}
                    />

                    <InputField
                      labelName="Data de Negociação:"
                      inputName="dataNegociacao"
                      inputMask="offmask"
                      inputType="date"
                      isRequired
                    />

                    <Select
                      options={medidaOptions}
                      label="Unidade de Medida:"
                      name="unidadeMedidaId"
                      placeholder="Unidade de Medida"
                      value={medidaSelecionada}
                      onChange={onSelectMedida}
                      isDisabled={isEdit}
                      isRequired
                      spacingControl={true}
                      errors={errors.unidadeMedidaId}
                      touched={touched.unidadeMedidaId}
                    />

                    <div className="flex gap-x-2">

                      <InputField
                        labelName="Volume Mapeado:"
                        tooltip="Volume total estimado para obra"
                        inputName="volumeMapeado"
                        inputMask="offmask"
                        inputType="number"
                        isRequired
                        placeholder="Volume Mapeado"
                        onWheel={(e) => e.target.blur()}
                      />

                      <InputField
                        labelName="Volume Negociado:"
                        tooltip="Volume em negociação"
                        inputName="volumeNegociado"
                        inputMask="offmask"
                        inputType="number"
                        isRequired
                        placeholder="Volume Negociado"
                        onWheel={(e) => e.target.blur()}
                      />

                    </div>

                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>

      <ModalAction
        isOpen={activeModal === modalKind.save}
        onClose={closeModal}
        title="Confirmar salvamento"
        sub="Deseja confirmar o salvamento dos dados?"
        status="success"
        buttonOne="Confirmar"
        buttonTwo="Cancelar"
        linkOne={confirmarSalvamento}
        linkTwo={closeModal}
      />
    </section>
  );
};

export default FunnelEdit;
